import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Navbar, Container, Form, NavDropdown, Nav,
    Offcanvas, Badge, InputGroup, ListGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCartShopping, faHeart, faBars, faUserCircle,
    faHome, faMessage, faRightFromBracket, faSearch,
    faLocationDot, faArrowLeft, faXmark, faBoxOpen,
    faStore, faCircleInfo
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { useCounter } from '../../contexts/counterContext';
import { useNotification } from '../../contexts/NotificationContext';
import Logo from '../../assets/logo3.png';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';

const NAV_STYLES = {
    navbar: {
        backgroundColor: 'rgba(255,255,255,0.98)',
        boxShadow: '0 2px 15px rgba(0,0,0,0.04)',
        borderBottom: '1px solid #f0f0f0',
        zIndex: 1050,
        position: 'sticky',
        top: 0,
    },
    navLink: {
        textTransform: 'uppercase',
        fontWeight: '700',
        color: '#282c3f',
        fontSize: '15px',
        letterSpacing: '0.8px',
        textDecoration: 'none',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    iconBtn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: '#282c3f',
        minWidth: '52px',
        minHeight: '52px',
        position: 'relative',
        background: 'transparent',
        border: 'none',
        padding: '6px',
    },
    icon: { fontSize: '1.3rem', color: '#282c3f' },
    badge: {
        position: 'absolute',
        top: '-2px',
        right: '-2px',
        backgroundColor: '#ff3f6c',
        fontSize: '0.6rem',
        minWidth: '16px',
        height: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        padding: '0 3px',
    },
    searchBox: {
        backgroundColor: '#f5f5f6',
        border: 'none',
        borderRadius: '8px',
    },
    searchResults: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: 'white',
        borderRadius: '0 0 8px 8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        zIndex: 1100,
        maxHeight: '300px',
        overflowY: 'auto',
        border: '1px solid #f0f0f0',
    },
};

/* ─── Search Suggestion List ─────────────────────────────── */
function SuggestionList({ products, onSelect }) {
    if (!products.length) return (
        <div className="p-3 text-center text-muted small">No products found</div>
    );
    return (
        <ListGroup variant="flush">
            {products.map(p => (
                <ListGroup.Item
                    key={p.id}
                    action
                    onClick={() => onSelect(p.id)}
                    className="border-0 d-flex align-items-center gap-3 py-2"
                >
                    <img
                        src={p.image_url || p.image || p.product_image_urls?.[0] || 'https://via.placeholder.com/40'}
                        alt="thumb"
                        style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 4 }}
                        loading="lazy"
                    />
                    <div>
                        <div className="fw-bold text-dark" style={{ fontSize: 13 }}>
                            {p.name || p.product_name}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                            {p.brand || p.brand_name || 'Generic'}
                        </div>
                    </div>
                </ListGroup.Item>
            ))}
        </ListGroup>
    );
}

/* ─── Main NavBar Component ──────────────────────────────── */
function NavBar({ onLocationClick }) {
    const navigate  = useNavigate();
    const location  = useLocation();
    const { isLoggedIn, logout, currentUser } = useAuth();
    const { getCartCount, wishlistItems } = useCounter();
    const { notify } = useNotification();

    const [userPincode, setUserPincode] = useState(
        localStorage.getItem('delivery_pincode')
    );
    const [allProducts, setAllProducts]         = useState([]);
    const [searchTerm, setSearchTerm]           = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const [showOffcanvas, setShowOffcanvas]     = useState(false);

    const desktopSearchRef = useRef(null);
    const mobileSearchRef  = useRef(null);

    /* Fetch products & locations once */
    useEffect(() => {
        const load = async () => {
            try {
                const snap = await getDocs(collection(db, 'products'));
                setAllProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (err) {
                console.error('Navbar fetch error:', err);
            }
        };
        load();

        const handler = (e) => {
            if (
                desktopSearchRef.current && !desktopSearchRef.current.contains(e.target) &&
                mobileSearchRef.current  && !mobileSearchRef.current.contains(e.target)
            ) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    /* Search logic */
    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        if (term.trim()) {
            const lo = term.toLowerCase();
            const res = allProducts.filter(p => {
                const name  = (p.name || p.product_name || '').toLowerCase();
                const brand = (p.brand || p.brand_name || '').toLowerCase();
                const cat   = (p.category || '').toLowerCase();
                return name.includes(lo) || brand.includes(lo) || cat.includes(lo);
            });
            setFilteredProducts(res.slice(0, 10));
            setShowSuggestions(true);
        } else {
            setFilteredProducts([]);
            setShowSuggestions(false);
        }
    };

    const handleProductSelect = (id) => {
        navigate(`/product/${id}`);
        setShowSuggestions(false);
        setSearchTerm('');
        setShowMobileSearch(false);
        setShowOffcanvas(false);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        const trimmed = searchTerm.trim();
        if (!trimmed) return;
        const lo = trimmed.toLowerCase();
        // If the term exactly matches a brand name, show ALL products of that brand
        const isBrand = allProducts.some(
            p => (p.brand || p.brand_name || '').toLowerCase() === lo
        );
        if (isBrand) {
            navigate(`/products?brand=${encodeURIComponent(trimmed)}`);
            setSearchTerm('');
            setShowSuggestions(false);
            setShowMobileSearch(false);
        } else if (filteredProducts.length > 0) {
            handleProductSelect(filteredProducts[0].id);
        }
    };

    const handleRestrictedNav = (path, label) => {
        if (isLoggedIn) {
            navigate(path);
        } else {
            notify(`Please login to view your ${label}`, 'info', {
                subtext: 'Sign in to continue',
                cta: 'Login',
                ctaAction: () => navigate('/login'),
            });
        }
        setShowOffcanvas(false);
    };

    const offcanvasLinks = [
        { label: 'Home',     path: '/',        icon: faHome        },
        { label: 'Shop',     path: '/products',icon: faStore       },
        { label: 'Feed',     path: '/feed',    icon: faMessage     },
        { label: 'How It Works', path: '/how-it-works', icon: faCircleInfo },
        ...(isLoggedIn ? [
            { label: 'Wishlist', path: '/wishlist',icon: faHeart       },
            { label: 'My Cart',  path: '/cart',    icon: faCartShopping},
            { label: 'Orders',   path: '/orders',  icon: faBoxOpen     },
            { label: 'Profile',  path: '/profile', icon: faUserCircle  },
        ] : []),
    ];

    const firstName = (currentUser?.displayName || currentUser?.name || '').split(' ')[0];

    return (
        <>
            {/* ─── Main Navbar ─────────────────────────────────────── */}
            <Navbar style={NAV_STYLES.navbar} className="bg-white py-2 py-lg-3">
                <Container fluid className="px-3 px-md-4 gap-2">

                    {/* LEFT: Logo + location (md+) + links (lg+) */}
                    <div className="d-flex align-items-center gap-2 gap-lg-4 flex-shrink-0">

                        {/* Logo */}
                        <Navbar.Brand
                            onClick={() => navigate('/')}
                            style={{ cursor: 'pointer', marginRight: 0, padding: 0 }}
                        >
                            <img
                                src={Logo}
                                alt="Celer"
                                style={{
                                    height: 'clamp(36px, 6vw, 60px)',
                                    width: 'auto',
                                    objectFit: 'contain',
                                    borderRadius: '50%',
                                }}
                            />
                        </Navbar.Brand>

                        {/* Back button — /products only */}
                        {location.pathname === '/products' && (
                            <div
                                className="d-none d-md-flex align-items-center nav-hover-effect"
                                style={{ ...NAV_STYLES.navLink, textTransform: 'none' }}
                                onClick={() => navigate('/')}
                            >
                                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                                Back
                            </div>
                        )}

                        {/* Location pill (md+) */}
                        <div
                            className="d-none d-md-flex align-items-center px-3 py-1 border rounded-pill"
                            style={{
                                borderColor: '#ff3f6c !important',
                                backgroundColor: '#fff0f3',
                                cursor: 'pointer',
                                border: '1px solid #ff3f6c',
                                transition: 'all 0.2s',
                                flexShrink: 0,
                            }}
                            onClick={onLocationClick}
                        >
                            <FontAwesomeIcon
                                icon={faLocationDot}
                                className="me-2"
                                style={{ color: '#ff3f6c', fontSize: 13 }}
                            />
                            <div style={{ lineHeight: 1.2 }}>
                                <span className="fw-bold d-block"
                                    style={{ fontSize: 11, color: '#ff3f6c', textTransform: 'uppercase' }}>
                                    {userPincode ? userPincode : 'Select Location'}
                                </span>
                                {userPincode && (
                                    <span style={{ fontSize: 9, color: '#696e79' }}>Tap to change</span>
                                )}
                            </div>
                        </div>

                        {/* Shop Now (lg+) */}
                        <div
                            className="d-none d-lg-block nav-hover-effect"
                            style={NAV_STYLES.navLink}
                            onClick={() => navigate('/products')}
                        >
                            Shop Now
                        </div>

                        {/* Feed (lg+) */}
                        <div
                            className="d-none d-lg-block nav-hover-effect"
                            style={NAV_STYLES.navLink}
                            onClick={() => navigate('/feed')}
                        >
                            Feed
                        </div>

                        {/* How It Works (lg+) */}
                        <div
                            className="d-none d-lg-block nav-hover-effect"
                            style={NAV_STYLES.navLink}
                            onClick={() => navigate('/how-it-works')}
                        >
                            How It Works
                        </div>
                    </div>

                    {/* MIDDLE: Desktop search (lg+) */}
                    <div
                        className="d-none d-lg-flex flex-grow-1 mx-3 mx-xl-4 position-relative"
                        ref={desktopSearchRef}
                        style={{ maxWidth: 480 }}
                    >
                        <Form
                            className="w-100"
                            onSubmit={handleSearchSubmit}
                        >
                            <InputGroup style={NAV_STYLES.searchBox}>
                                <InputGroup.Text className="bg-transparent border-0 ps-3">
                                    <FontAwesomeIcon icon={faSearch} style={{ color: '#94969f' }} />
                                </InputGroup.Text>
                                <Form.Control
                                    type="search"
                                    placeholder="Search products, brands and more"
                                    className="bg-transparent border-0 shadow-none"
                                    style={{ fontSize: 14, color: '#696e79' }}
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    onFocus={() => searchTerm && setShowSuggestions(true)}
                                />
                            </InputGroup>
                        </Form>

                        {showSuggestions && (
                            <div style={NAV_STYLES.searchResults}>
                                <SuggestionList
                                    products={filteredProducts}
                                    onSelect={handleProductSelect}
                                />
                            </div>
                        )}
                    </div>

                    {/* RIGHT: icon buttons + hamburger */}
                    <div className="d-flex align-items-center gap-1 gap-md-2 ms-auto flex-shrink-0">

                        {/* Mobile search toggle (<lg) */}
                        <button
                            className="d-lg-none"
                            style={NAV_STYLES.iconBtn}
                            onClick={() => { setShowMobileSearch(p => !p); setShowSuggestions(false); }}
                            aria-label="Toggle search"
                        >
                            <FontAwesomeIcon
                                icon={showMobileSearch ? faXmark : faSearch}
                                style={NAV_STYLES.icon}
                            />
                        </button>

                        {/* Cart — always visible */}
                        <button
                            style={NAV_STYLES.iconBtn}
                            onClick={() => handleRestrictedNav('/cart', 'Cart')}
                            aria-label="Cart"
                        >
                            <FontAwesomeIcon icon={faCartShopping} style={NAV_STYLES.icon} />
                            <span className="d-none d-lg-block" style={{ fontSize: 12, fontWeight: 600 }}>Cart</span>
                            {getCartCount > 0 && (
                                <span style={NAV_STYLES.badge}>{getCartCount}</span>
                            )}
                        </button>

                        {/* Wishlist (md+) */}
                        <button
                            className="d-none d-md-flex"
                            style={NAV_STYLES.iconBtn}
                            onClick={() => handleRestrictedNav('/wishlist', 'Wishlist')}
                            aria-label="Wishlist"
                        >
                            <FontAwesomeIcon icon={faHeart} style={NAV_STYLES.icon} />
                            <span className="d-none d-lg-block" style={{ fontSize: 12, fontWeight: 600 }}>Wishlist</span>
                            {wishlistItems.length > 0 && (
                                <span style={NAV_STYLES.badge}>{wishlistItems.length}</span>
                            )}
                        </button>

                        {/* Profile (md+) */}
                        <div
                            className="d-none d-md-flex align-items-center"
                            style={{ ...NAV_STYLES.iconBtn, flexDirection: 'column' }}
                            onClick={() => !isLoggedIn && navigate('/login')}
                        >
                            <FontAwesomeIcon icon={faUserCircle} style={NAV_STYLES.icon} />
                            {isLoggedIn ? (
                                <NavDropdown
                                    title={
                                        <span style={{ color: '#282c3f', fontWeight: 600, fontSize: 12 }}>
                                            {firstName ? `Hi, ${firstName}` : 'Profile'}
                                        </span>
                                    }
                                    id="profile-dropdown"
                                    align="end"
                                    style={{ marginTop: '-4px' }}
                                >
                                    <NavDropdown.Item onClick={() => navigate('/profile')}>
                                        My Profile
                                    </NavDropdown.Item>
                                    <NavDropdown.Item onClick={() => navigate('/orders')}>
                                        My Orders
                                    </NavDropdown.Item>
                                    <NavDropdown.Divider />
                                    <NavDropdown.Item
                                        onClick={() => { logout(); navigate('/login'); }}
                                        className="text-danger"
                                    >
                                        Logout
                                    </NavDropdown.Item>
                                </NavDropdown>
                            ) : (
                                <span style={{ fontSize: 12, fontWeight: 600 }}>Login</span>
                            )}
                        </div>

                        {/* Hamburger (<md) */}
                        <button
                            className="d-md-none"
                            style={NAV_STYLES.iconBtn}
                            onClick={() => setShowOffcanvas(true)}
                            aria-label="Open menu"
                        >
                            <FontAwesomeIcon icon={faBars} style={{ ...NAV_STYLES.icon, fontSize: '1.25rem' }} />
                        </button>
                    </div>
                </Container>

                {/* ─── Mobile search row (xs/sm/md) ─────────────────── */}
                {showMobileSearch && (
                    <div
                        ref={mobileSearchRef}
                        className="d-lg-none w-100 px-3 pb-3 pt-2"
                        style={{ background: '#fff', borderTop: '1px solid #f5f5f6' }}
                    >
                        <div className="position-relative">
                            <Form
                                onSubmit={handleSearchSubmit}
                            >
                                <InputGroup style={NAV_STYLES.searchBox}>
                                    <InputGroup.Text className="bg-transparent border-0 ps-3">
                                        <FontAwesomeIcon icon={faSearch} style={{ color: '#94969f' }} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="search"
                                        placeholder="Search products, brands..."
                                        className="bg-transparent border-0 shadow-none"
                                        style={{ fontSize: 14, color: '#696e79' }}
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        onFocus={() => searchTerm && setShowSuggestions(true)}
                                        autoFocus
                                    />
                                </InputGroup>
                            </Form>
                            {showSuggestions && filteredProducts.length > 0 && (
                                <div style={{ ...NAV_STYLES.searchResults, position: 'absolute' }}>
                                    <SuggestionList
                                        products={filteredProducts}
                                        onSelect={handleProductSelect}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Navbar>

            {/* ─── Mobile Offcanvas Menu (<md) ─────────────────────── */}
            <Offcanvas
                show={showOffcanvas}
                onHide={() => setShowOffcanvas(false)}
                placement="end"
                className="d-md-none"
                style={{ maxWidth: '85vw', width: 300 }}
            >
                <Offcanvas.Header closeButton className="border-bottom px-4 py-3">
                    <Offcanvas.Title>
                        <img src={Logo} alt="Celer" style={{ height: 36, borderRadius: '50%' }} />
                    </Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body className="p-0 d-flex flex-column">
                    {/* Location */}
                    <div
                        className="d-flex align-items-center px-4 py-3 border-bottom"
                        style={{ cursor: 'pointer' }}
                        onClick={() => { onLocationClick(); setShowOffcanvas(false); }}
                    >
                        <FontAwesomeIcon icon={faLocationDot} className="me-3" style={{ color: '#ff3f6c', fontSize: 20, width: 22 }} />
                        <div>
                            <div className="fw-bold text-dark" style={{ fontSize: 14 }}>
                                {userPincode ? `Delivering to ${userPincode}` : 'Select Location'}
                            </div>
                            <div className="text-muted" style={{ fontSize: 11 }}>Tap to change</div>
                        </div>
                    </div>

                    {/* User section */}
                    {isLoggedIn ? (
                        <div className="d-flex align-items-center gap-3 px-4 py-3 border-bottom bg-light">
                            <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '2rem', color: '#282c3f' }} />
                            <div>
                                <div className="fw-bold" style={{ fontSize: 14 }}>
                                    {currentUser?.displayName || currentUser?.name || 'User'}
                                </div>
                                <div className="text-muted" style={{ fontSize: 11 }}>
                                    {currentUser?.email || currentUser?.phone || ''}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="px-4 py-3 border-bottom">
                            <button
                                className="btn btn-dark w-100 py-2 fw-bold rounded-3"
                                onClick={() => { navigate('/login'); setShowOffcanvas(false); }}
                            >
                                Login / Sign Up
                            </button>
                        </div>
                    )}

                    {/* Nav links */}
                    <div className="flex-grow-1 overflow-auto">
                        {offcanvasLinks.map(item => (
                            <div
                                key={item.path}
                                className="d-flex align-items-center px-4 py-3 border-bottom"
                                style={{ cursor: 'pointer', fontSize: 15, fontWeight: 500 }}
                                onClick={() => { navigate(item.path); setShowOffcanvas(false); }}
                            >
                                <FontAwesomeIcon
                                    icon={item.icon}
                                    className="me-3 text-muted"
                                    style={{ width: 20 }}
                                />
                                {item.label}
                                {item.path === '/cart' && getCartCount > 0 && (
                                    <Badge bg="danger" pill className="ms-auto" style={{ fontSize: 10 }}>
                                        {getCartCount}
                                    </Badge>
                                )}
                                {item.path === '/wishlist' && wishlistItems.length > 0 && (
                                    <Badge bg="danger" pill className="ms-auto" style={{ fontSize: 10 }}>
                                        {wishlistItems.length}
                                    </Badge>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Logout */}
                    {isLoggedIn && (
                        <div className="px-4 py-3 border-top mt-auto">
                            <button
                                className="btn btn-outline-danger w-100 py-2 fw-bold"
                                onClick={() => { logout(); navigate('/login'); setShowOffcanvas(false); }}
                            >
                                <FontAwesomeIcon icon={faRightFromBracket} className="me-2" />
                                Sign Out
                            </button>
                        </div>
                    )}
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
}

export default NavBar;
